import React, { useState, useEffect } from 'react';
import { Alert, Card, List, Spin, Typography, Button, Result, message, Image, Pagination, Modal } from 'antd';
import { QrcodeOutlined } from '@ant-design/icons';
import QRCode from 'react-qr-code';
import eventBus from '../utils/eventBus';
import EditGalleryModal from '../components/EditGalleryModal';
import { useTranslation } from '../hooks/useTranslation';

const { Text } = Typography;

const Galleries: React.FC = () => {
  const { t } = useTranslation();
  const [galleries, setGalleries] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [currentGalleryId, setCurrentGalleryId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(12);

  // 二维码相关状态
  const [qrcodeModalOpen, setQrcodeModalOpen] = useState<boolean>(false);
  const [qrcodeUrl, setQrcodeUrl] = useState<string>('');

  // 获取用户图集数据
  const fetchGalleries = async () => {
    try {
      const response = await fetch('/gallery/list', {
        method: 'GET',
        credentials: 'include', // 确保携带cookie
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // 检查响应的内容类型
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json();
        
        if (result.success) {
          setGalleries(result.data);
          setErrorCode(null);
        } else {
          // 如果返回错误信息，设置错误状态（code 供前端区分是否需登录等）
          setError(result.message || t('galleries.loadFailed'));
          setErrorCode(result.code || null);
        }
      } else {
        // 如果响应不是JSON格式，设置错误状态
        setError(t('galleries.loadFailedFormat'));
        setErrorCode(null);
      }
    } catch (err) {
      // 网络错误或其他异常
      setError(t('galleries.loadFailedNetwork'));
      setErrorCode(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGalleries();
  }, []);

  // 获取图集的封面图（优先使用bannerUrl，否则使用第一张图片）
  const getCoverImage = (gallery: any) => {
    if (gallery.bannerUrl) {
      return gallery.bannerUrl;
    }
    // 如果有images字段，解析JSON获取第一张图片
    if (gallery.images) {
      try {
        const images = JSON.parse(gallery.images);
        if (images && images.length > 0) {
          return images[0];
        }
      } catch (e) {
        console.error('解析images失败', e);
      }
    }
    return null;
  };

  const handleOpenGallery = (galleryUrl: string) => {
    // 使用后端返回的完整访问链接
    window.open(galleryUrl, '_blank');
  };

  const handleManageGallery = (galleryId: string) => {
    // 打开编辑弹窗
    setCurrentGalleryId(galleryId);
    setEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    // 编辑成功后刷新列表
    fetchGalleries();
  };

  const handleDeleteGallery = (galleryId: string) => {
    // 删除图集
    const doDelete = window.confirm(t('galleries.deleteConfirm'));
    if (doDelete) {
      fetch(`/gallery/${galleryId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })
      .then(response => response.json())
      .then(result => {
        if (result.success) {
          message.success(t('galleries.deleteSuccess'));
          // 局部刷新，只重新加载图集列表
          fetchGalleries();
        } else {
          message.error(t('galleries.deleteFailed', { message: result.message || t('galleries.unknownError') }));
        }
      })
      .catch(error => {
        message.error(t('galleries.deleteFailedNetwork'));
      });
    }
  };

  const handleLogin = () => {
    // 使用已导入的 EventBus
    eventBus.emit('openLoginModal');
  };

  // 显示二维码
  const handleShowQRCode = (galleryUrl: string) => {
    setQrcodeUrl(galleryUrl);
    setQrcodeModalOpen(true);
  };

  // 计算分页数据
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedGalleries = galleries.slice(startIndex, endIndex);

  // 分页改变处理
  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); // 改变每页条数时重置到第一页
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>{t('galleries.loading')}</p>
      </div>
    );
  }

  // 根据错误码或文案判断为「需登录」时，显示登录引导（兼容多语言与旧接口无 code 的情况）
  const isLoginRequired = errorCode === 'common.login.required' ||
    (error && (error.includes('请先登录') || error.includes('Please login first')));
  if (error && isLoginRequired) {
    return (
      <div style={{ padding: '20px' }}>
        <Result
          status="warning"
          title={t('galleries.loginRequired')}
          subTitle={t('galleries.loginRequiredSubtitle')}
          extra={
            <Button type="primary" onClick={handleLogin}>
              {t('galleries.goToLogin')}
            </Button>
          }
        />
      </div>
    );
  }

  // 如果有其他错误，显示错误提示
  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert message={t('galleries.error')} description={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div style={{ padding: '12px' }}>
      <Alert
        message={t('galleries.loginRequiredNotice')}
        type="info"
        showIcon
        style={{ marginBottom: '12px' }}
      />

      {galleries.length === 0 ? (
        <Card size="small">
          <Text>{t('galleries.empty')}</Text>
        </Card>
      ) : (
        <>
          <List
            grid={{
              gutter: 10,
              xs: 1,
              sm: 2,
              md: 3,
              lg: 4,
              xl: 5,
              xxl: 6
            }}
            dataSource={paginatedGalleries}
            renderItem={(gallery) => {
            const coverImage = getCoverImage(gallery);
            return (
              <List.Item>
                <Card
                  hoverable
                  size="small"
                  styles={{
                    body: { padding: '8px' }
                  }}
                  style={{
                    border: '1px solid #e8e8e8',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s'
                  }}
                  cover={
                    coverImage ? (
                      <div style={{
                        height: '140px',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5'
                      }}>
                        <Image
                          src={coverImage}
                          alt={gallery.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          preview={false}
                        />
                      </div>
                    ) : (
                      <div style={{
                        height: '140px',
                        backgroundColor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '12px'
                      }}>
                        {t('galleries.noCover')}
                      </div>
                    )
                  }
                >
                  <Card.Meta
                    title={
                      <div style={{
                        fontSize: '14px',
                        marginBottom: '4px',
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: 'nowrap'
                      }}>
                        {gallery.title}
                      </div>
                    }
                    description={
                      <div>
                        <div style={{
                          marginBottom: '4px',
                          fontSize: '12px',
                          lineHeight: '1.3',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {gallery.description || t('galleries.noDescription')}
                        </div>
                        <div style={{ fontSize: '11px', color: '#999', display: 'flex', justifyContent: 'space-between' }}>
                          <span>{t('galleries.countUnit', { count: gallery.imageCount })}</span>
                          <span>{new Date(gallery.createTime).toLocaleDateString()}</span>
                        </div>
                      </div>
                    }
                  />
                  <div style={{ marginTop: '8px', display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => handleOpenGallery(gallery.galleryUrl)}
                    >
                      {t('galleries.open')}
                    </Button>
                    <Button
                      size="small"
                      icon={<QrcodeOutlined />}
                      onClick={() => handleShowQRCode(gallery.galleryUrl)}
                      title={t('galleries.viewQrCode')}
                    />
                    <Button
                      size="small"
                      onClick={() => handleManageGallery(gallery.galleryId)}
                    >
                      {t('galleries.edit')}
                    </Button>
                    <Button
                      size="small"
                      danger
                      onClick={() => handleDeleteGallery(gallery.galleryId)}
                    >
                      {t('galleries.delete')}
                    </Button>
                  </div>
                </Card>
              </List.Item>
            );
          }}
        />

        {/* 分页控件 */}
        {galleries.length > pageSize && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={galleries.length}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              showSizeChanger
              showQuickJumper
              showTotal={(total) => t('galleries.totalGalleries', { count: total })}
              pageSizeOptions={['12', '24', '48', '96']}
              size="small"
            />
          </div>
        )}
        </>
      )}

      {/* 编辑图集弹窗 */}
      <EditGalleryModal
        open={editModalOpen}
        galleryId={currentGalleryId}
        onClose={() => setEditModalOpen(false)}
        onSuccess={handleEditSuccess}
      />

      {/* 二维码弹窗 */}
      <Modal
        title={t('galleries.qrCodeTitle')}
        open={qrcodeModalOpen}
        onCancel={() => setQrcodeModalOpen(false)}
        footer={null}
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          {qrcodeUrl && (
            <>
              <div style={{
                padding: '16px',
                background: '#ffffff',
                borderRadius: '8px',
                display: 'inline-block',
                marginBottom: '16px'
              }}>
                <QRCode
                  value={qrcodeUrl}
                  size={256}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  level="H"
                />
              </div>
              <div style={{ color: '#666', fontSize: '14px', lineHeight: '1.6' }}>
                <p>{t('galleries.scanHint1')}</p>
                <p>{t('galleries.scanHint2')}</p>
              </div>
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: '#f9f9f9',
                borderRadius: '4px',
                wordBreak: 'break-all',
                fontSize: '12px',
                color: '#999'
              }}>
                {qrcodeUrl}
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Galleries;